import { useState, useEffect, useMemo, useCallback } from "react";
import { networkPillLabel } from "@/lib/networkLabels";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowsClockwise, CaretLeft, CaretRight, Columns, CurrencyDollar, DownloadSimple, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import DateRangePresets from "@/components/DateRangePresets";
import BrandCombobox from "@/components/BrandCombobox";
import MultiSelect from "@/components/MultiSelect";
import FeedPartnerSelect, { appendNetworkParam, FEED_PARTNER_OPTIONS } from "@/components/FeedPartnerSelect";

const PAGE_SIZE = 100;
// Reports open with Impact excluded; the user can re-tick it in the filter.
// The dropdown shows every other partner ticked, but that exact selection is
// sent as `exclude_network=impact` rather than an allowlist, so rows with no
// network (and feeds added later) are not hidden by the default.
const DEFAULT_NETWORK_FILTER = FEED_PARTNER_OPTIONS
  .map((o) => o.value)
  .filter((v) => v !== "all" && v !== "impact")
  .join(",");

// PENDING / APPROVED / REVERSED are canonical billing states and go to
// `?state=`; NON_COMMISSION / BONUS are FlexOffers upstream labels and go
// to `?network_state=`. buildParams splits them apart.
const STATE_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REVERSED", label: "Reversed" },
  { value: "NON_COMMISSION", label: "Non-Commission" },
  { value: "BONUS", label: "Bonus" },
];
const COLUMNS_STORAGE_KEY = "admin-reports-visible-columns-v1";

/** Column catalog for Reports & Actions. `defaultOn` = shown until admin toggles. */
const REPORT_COLUMNS = [
  { id: "action_id", label: "Action ID", defaultOn: true, align: "left" },
  { id: "brand", label: "Brand", defaultOn: true, align: "left" },
  { id: "network", label: "Network", defaultOn: true, align: "left" },
  { id: "publisher", label: "Publisher", defaultOn: true, align: "left" },
  { id: "sub_id", label: "SubID", defaultOn: true, align: "left" },
  { id: "state", label: "State", defaultOn: true, align: "left" },
  { id: "amount", label: "Sale Amount", defaultOn: true, align: "right" },
  { id: "payout", label: "Network Payout", defaultOn: true, align: "right" },
  { id: "network_pct", label: "Network %", defaultOn: false, align: "right", optional: true },
  { id: "pub_commission_pct", label: "Pub Commission %", defaultOn: false, align: "right", optional: true },
  { id: "pub_commission", label: "Pub Commission", defaultOn: false, align: "right", optional: true },
  { id: "pub_earning", label: "Pub Earning", defaultOn: false, align: "right", optional: true },
  { id: "margin", label: "Margin", defaultOn: false, align: "right", optional: true },
  { id: "margin_pct", label: "Margin %", defaultOn: false, align: "right", optional: true },
  { id: "country", label: "Country", defaultOn: true, align: "left" },
  { id: "date", label: "Date", defaultOn: true, align: "left" },
];

function loadVisibleColumns() {
  const defaults = Object.fromEntries(REPORT_COLUMNS.map((c) => [c.id, c.defaultOn]));
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

/** ISO-2 country code → flag emoji. Empty string in, empty out. */
function iso2ToFlag(iso2) {
  if (!iso2 || iso2.length !== 2) return "";
  const base = 0x1F1E6;
  return String.fromCodePoint(
    base + iso2.toUpperCase().charCodeAt(0) - 65,
    base + iso2.toUpperCase().charCodeAt(1) - 65,
  );
}

function fmtMoney(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(2)}%`;
}

/** True when value looks like a RedClub publisher sub_id (not a URL / RID). */
function isPublisherSubId(value) {
  if (!value || !String(value).trim()) return false;
  const s = String(value).trim().toUpperCase();
  if (s.includes("://") || s.includes("/") || s.includes(" ") || s.length > 20) return false;
  return /^[A-Z0-9]{4,12}$/.test(s);
}

/**
 * Display SubID for an action row.
 * Siteplug stores click RID in sub_id1 and publisher sub_id in sub_id3
 * (or enriched publisher_sub_id). Never show RID in the SubID column.
 */
function subIdForAction(a) {
  const net = (a.network || "").toLowerCase();
  if (net === "siteplug") {
    if (a.publisher_sub_id) return String(a.publisher_sub_id).toUpperCase();
    const s3 = String(a.sub_id3 || "").trim();
    if (isPublisherSubId(s3)) return s3.toUpperCase();
    return "";
  }
  return a.sub_id1 || "";
}

/**
 * Admin → Reports & Actions
 *
 * Single-source-of-truth view of every conversion across all networks.
 * Server-side filtering + pagination so the UI scales past the legacy
 * 5k-row cap. Mirrors what publishers see in PubReports → click-log.
 */
export default function AdminReports() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, reversed: 0, payout: 0 });
  const [page, setPage] = useState(1);
  const [publishers, setPublishers] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [foSyncing, setFoSyncing] = useState(false);
  const [snSyncing, setSnSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 0 = no progress shown; a large export fetches 1k rows at a time and can
  // run for a minute, so the button reports how far along it is.
  const [exportProgress, setExportProgress] = useState(0);
  const [visibleCols, setVisibleCols] = useState(loadVisibleColumns);

  // Filters — sent server-side every page fetch.
  // Arrays, not scalars — these three are checkbox multi-select. Empty
  // means "all", which is also what the API treats as no filter.
  const [pubFilter, setPubFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [networkFilter, setNetworkFilter] = useState(DEFAULT_NETWORK_FILTER);
  const [dateRange, setDateRange] = useState({ preset: "all", start: "", end: "" });
  // Off by default: $0 PENDING/APPROVED conversions are hidden, matching what
  // publishers see. On: every row, for reconciling with a network.
  const [showZero, setShowZero] = useState(false);

  const toggleColumn = useCallback((id) => {
    setVisibleCols((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch { /* ignore quota */ }
      return next;
    });
  }, []);

  const publisherOptions = useMemo(
    () => publishers
      .filter((p) => p.role === "publisher" || !p.role)
      .map((p) => ({ value: p.id, label: p.email, hint: p.sub_id || "" })),
    [publishers],
  );

  const activeColumns = useMemo(
    () => REPORT_COLUMNS.filter((c) => visibleCols[c.id]),
    [visibleCols],
  );

  const buildParams = useCallback((overrides = {}) => {
    const params = new URLSearchParams();
    // `append`, not `set` — repeated params are how the API takes several
    // values. Commas are not an option for brands: real campaign names
    // contain them ("Bluemercury, Inc.").
    pubFilter.forEach((id) => params.append("publisher_id", id));
    brandFilter.forEach((b) => params.append("brand", b));
    // stateFilter is overloaded — canonical billing values (PENDING /
    // APPROVED / REVERSED) hit the `state` query param; FlexOffers
    // 5-bucket labels (NON_COMMISSION / BONUS) route to `network_state`
    // so the row-level filter targets the upstream-label field. The
    // backend accepts both params independently, and both are multi-value,
    // so a mixed selection (Approved + Non-Commission) now works — it
    // used to be unrepresentable with one dropdown.
    stateFilter.forEach((v) => {
      if (v === "NON_COMMISSION") params.append("network_state", "Non-Commission");
      else if (v === "BONUS") params.append("network_state", "Bonus");
      else params.append("state", v);
    });
    if (networkFilter === DEFAULT_NETWORK_FILTER) params.set("exclude_network", "impact");
    else appendNetworkParam(params, networkFilter);
    if (dateRange.start) params.set("start_date", dateRange.start);
    if (dateRange.end) params.set("end_date", dateRange.end);
    if (showZero) params.set("include_zero", "1");
    Object.entries(overrides).forEach(([k, v]) => params.set(k, v));
    return params;
  }, [pubFilter, brandFilter, stateFilter, networkFilter, dateRange, showZero]);

  const fetchActions = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const params = buildParams({ page: targetPage, page_size: PAGE_SIZE });
      const { data } = await API.get(`/admin/actions?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, pending: 0, approved: 0, reversed: 0, payout: 0 });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load actions");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchPublishers = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/publishers");
      setPublishers(data || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchFacets = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/actions/facets");
      setBrandOptions(data.brands || []);
    } catch (err) { console.error(err); }
  }, []);

  const syncActions = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post("/admin/actions/sync", {});
      const i = data.impact?.synced ?? 0;
      const p = data.partnerize?.synced ?? 0;
      const s = data.skimlinks?.synced ?? 0;
      toast.success(`Synced ${data.synced} actions · ${i} Impact · ${p} Partnerize · ${s} Skimlinks`);
      fetchActions(1);
      setPage(1);
      fetchFacets();
    } catch (err) {
      toast.error("Failed to sync actions");
    } finally {
      setSyncing(false);
    }
  };

  // Re-pull FlexOffers conversions for the selected date range and
  // OVERRIDE existing commission/payout values with the latest from the
  // FlexOffers API. FlexOffers restates commissions after the fact
  // (Pending → Approved / Non-Commission, amount corrections), so a
  // stale row can carry an outdated commission until it's re-synced.
  // Scoped by date range to keep the pull bounded — the endpoint upserts
  // with `$set` so existing rows are overwritten, not duplicated.
  const syncFlexOffers = async () => {
    if (foSyncing) return;
    if (!dateRange.start || !dateRange.end) {
      toast.error("Pick a date range first — the FlexOffers commission re-sync overrides existing values for the selected window.");
      return;
    }
    if (!window.confirm(
      `Re-sync FlexOffers commissions for ${dateRange.start} → ${dateRange.end}?\n\n`
      + "This overrides existing commission/payout values for FlexOffers conversions in this window with the latest data from the FlexOffers API.",
    )) return;
    setFoSyncing(true);
    try {
      const { data } = await API.post("/admin/flexoffers/sync-actions", {
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      toast.success(
        `FlexOffers: re-synced ${(data.synced ?? 0).toLocaleString()} conversion${data.synced === 1 ? "" : "s"} `
        + `(${dateRange.start} → ${dateRange.end}) — commissions overridden with latest.`,
      );
      fetchActions(1);
      setPage(1);
      fetchFacets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "FlexOffers commission sync failed");
    } finally {
      setFoSyncing(false);
    }
  };

  const syncShopnomix = async () => {
    if (snSyncing) return;
    setSnSyncing(true);
    try {
      const params = {};
      // Use the Reports date filter for historical backfill. If "All time"
      // (no range), pull last 30 days (matches nightly cron). Cap is 90d
      // on the server — set an explicit range for older history.
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;
      if (!dateRange.start && !dateRange.end) params.lookback_days = 30;
      const windowLabel = dateRange.start && dateRange.end
        ? `${dateRange.start} → ${dateRange.end}`
        : "last 30 days";
      const { data } = await API.post("/admin/shopnomix/sync-report", null, { params });
      if (data.status === "queued" && data.run_id) {
        toast.loading(`Shopnomix sync (${windowLabel}) running…`, { id: "sn-sync" });
        const deadline = Date.now() + 15 * 60 * 1000;
        let final = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2500));
          const { data: run } = await API.get(`/admin/shopnomix/sync-report/${data.run_id}`);
          if (run.status === "ok" || run.status === "failed") {
            final = run;
            break;
          }
        }
        if (!final) {
          toast.error("Shopnomix sync still running — refresh Reports in a minute", { id: "sn-sync" });
        } else if (final.status === "failed") {
          toast.error(final.error || "Shopnomix report sync failed", { id: "sn-sync" });
        } else {
          const c = final.counts || {};
          toast.success(
            `Shopnomix (${windowLabel}): ${(c.rows_seen ?? 0).toLocaleString()} row(s) · `
            + `${c.upserted ?? 0} new · ${c.modified ?? 0} updated`
            + (c.auto_approved != null ? ` · ${c.auto_approved} auto-approved` : ""),
            { id: "sn-sync" },
          );
        }
      } else {
        toast.success(
          `Shopnomix: ${(data.rows_seen ?? 0).toLocaleString()} row(s) · `
          + `${data.upserted ?? 0} new · ${data.modified ?? 0} updated`
          + (data.auto_approved != null ? ` · ${data.auto_approved} auto-approved` : ""),
        );
      }
      fetchActions(1);
      setPage(1);
      fetchFacets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Shopnomix report sync failed");
    } finally {
      setSnSyncing(false);
    }
  };

  // Reset to page 1 + refetch whenever a filter changes.
  useEffect(() => { setPage(1); fetchActions(1); }, [fetchActions]);
  useEffect(() => { fetchPublishers(); fetchFacets(); }, [fetchPublishers, fetchFacets]);

  // Build a sub_id → publisher map (incl. slot sub_ids) so the table can
  // display the publisher email next to each action's sub_id1.
  const subIdToPub = useMemo(() => {
    const m = new Map();
    for (const p of publishers) {
      if (p.sub_id) m.set(p.sub_id, p);
      for (const s of (p.slot_sub_ids || [])) {
        if (s.sub_id) m.set(s.sub_id, p);
      }
    }
    return m;
  }, [publishers]);

  const publisherForAction = useCallback((a) => {
    if (a.publisher_email) return a.publisher_email;
    const key = subIdForAction(a);
    return (key && subIdToPub.get(key)?.email) || "";
  }, [subIdToPub]);

  // ONE request. The server streams the CSV; we just save it.
  //
  // This used to page /admin/actions 1,000 rows at a time — 62 HTTP round
  // trips for a 61k-row export, each one re-running count_documents AND a
  // 20-accumulator stats aggregation that was thrown away, with every row
  // held in browser memory until the end. It also needed keyset pagination
  // and an action_id de-dup map purely to survive its own paging: a real
  // export returned 41,927 rows holding only 40,899 distinct actions.
  //
  // A single server-side cursor makes duplicates and gaps impossible rather
  // than merely detectable, and the row ceiling comes from the shared tier
  // policy (services/csv_export.py) instead of a constant hardcoded here.
  const exportCsv = async () => {
    if (!total) return;
    setExporting(true);
    try {
      const params = buildParams({});
      // Only the columns the admin actually has visible.
      const cols = activeColumns.map((c) => ({
        action_id: "action_id", brand: "campaign", network: "network",
        publisher: "publisher", sub_id: "sub_id1", state: "state",
        amount: "sale_amount", payout: "network_payout",
        network_pct: "network_pct", pub_commission_pct: "pub_commission_pct",
        pub_commission: "pub_commission", pub_earning: "pub_earning",
        margin: "margin", margin_pct: "margin_pct",
        country: "brand_country", date: "event_date",
      }[c.id] || c.id));

      // Every money column travels with its reversal-netted mirror.
      //
      // The tiles above net reversals OUT of the money but leave them IN the
      // row count, so summing a gross-only column in a spreadsheet can never
      // reproduce the number on screen — the file and the panel disagree by
      // the reversed total for the very same transactions. The mirrors are
      // not separately selectable in the column menu on purpose: an admin
      // asking for "payout" wants a file whose payout reconciles, not a
      // second checkbox they have to know to tick.
      const NET_MIRROR = {
        sale_amount: "net_sale_amount",
        network_payout: "net_payout",
        pub_earning: "net_pub_earning",
      };
      cols.forEach((c) => {
        const mirror = NET_MIRROR[c];
        if (mirror && !cols.includes(mirror)) cols.push(mirror);
      });
      params.set("columns", cols.join(","));

      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(
        `${baseURL}/admin/actions/export.csv?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!resp.ok) {
        // 413 carries the tier policy's own message — show it verbatim
        // rather than a generic failure.
        let detail = `HTTP ${resp.status}`;
        try { detail = (await resp.json())?.detail || detail; } catch { /* not json */ }
        toast.error(detail);
        return;
      }

      // Stream to a Blob, counting ROWS (newlines) rather than bytes, so
      // the percentage is against the server's own X-Total-Count instead of
      // a guessed bytes-per-MB constant. Newline counting is approximate —
      // a quoted field may legitimately contain one — hence the 99% clamp;
      // the authoritative count is the footer's, read once at the end.
      const expected = parseInt(resp.headers.get("X-Total-Count") || "0", 10);
      const reader = resp.body.getReader();
      const chunks = [];
      let lines = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        for (let i = 0; i < value.length; i += 1) if (value[i] === 10) lines += 1;
        if (expected > 0) {
          // -1 for the header line; the footer is not counted until the end.
          setExportProgress(Math.min(99, Math.round(((lines - 1) / expected) * 100)));
        }
      }
      const blob = new Blob(chunks, { type: "text/csv" });

      // Refuse a truncated file instead of saving it as a success.
      //
      // A StreamingResponse commits HTTP 200 before the first row, so a
      // Mongo cursor that dies mid-scan or a proxy read timeout on a long
      // export arrives as an ordinary end-of-stream — `resp.ok` was true and
      // the reader simply stopped. The old client-side paging loop caught
      // this with `all.length < serverTotal`; nothing replaced it. The
      // server writes a `# ...rows=N...` provenance footer as its LAST line
      // (services/csv_export.py `_footer`), so its presence is the
      // end-of-stream marker and its count is authoritative.
      const tail = await blob.slice(Math.max(0, blob.size - 8192)).text();
      const footer = tail.trimEnd().split("\n").pop() || "";
      if (!footer.startsWith("#")) {
        toast.error(
          "Export incomplete — the download was cut short before the server "
          + "finished. Narrow the date range or filters and try again.",
        );
        return;
      }
      const wrote = parseInt((footer.match(/rows=(\d+)/) || [])[1] || "0", 10);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `admin_reports_${new Date().toISOString().slice(0, 10)}.csv`;
      // Firefox only fires a download for an anchor that is IN the document;
      // Chrome does not care. Same three lines as AdminClickLog / AdminBrands.
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${wrote.toLocaleString()} row${wrote === 1 ? "" : "s"}`);
    } catch (err) {
      console.error(err);
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Filters whose result set contains nothing but canonically-REVERSED
  // rows. Non-Commission and Cancelled sit in this dropdown right next to
  // Reversed and both map to state=REVERSED upstream (admin_flexoffers.py),
  // so the backend puts the money tiles on a gross basis for all three —
  // net-of-reversals over a reversed-only set is $0.00. Keep this in step
  // with `_REVERSED_NETWORK_STATES` / `showing_reversed_only` in
  // routers/admin_reports.py.
  // Every selected value, not any — mixing Approved in puts non-reversed
  // money back in the set. Mirrors `showing_reversed_only` in
  // routers/admin_reports.py, which flips on the same condition.
  const isReversedOnlyFilter = (
    stateFilter.length > 0
    && stateFilter.every((v) => v === "REVERSED" || v === "NON_COMMISSION")
  );

  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;
  const showingFrom = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(p, totalPages));
    setPage(next);
    fetchActions(next);
  };

  return (
    <div className="p-8" data-testid="admin-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Conversions / Ledger
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Reports & Actions</h1>
          <p className="text-sm text-slate-500 mt-2" data-testid="admin-reports-summary">
            {(stats.total ?? total).toLocaleString()} action{(stats.total ?? total) === 1 ? "" : "s"} match current filters · {networkFilter === "all" ? "all feed partners" : networkFilter === DEFAULT_NETWORK_FILTER ? "all except Impact" : networkFilter}
          </p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer whitespace-nowrap"
            title="$0 pending/approved conversions are hidden by default, as in publisher reports"
            data-testid="show-zero-toggle">
            <input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} />
            Show $0 conversions
          </label>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!total || exporting} data-testid="export-csv-btn">
            <DownloadSimple size={14} className="mr-1.5" />
            {exporting ? (exportProgress ? `Exporting ${exportProgress}%` : "Exporting…") : "CSV"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={syncFlexOffers}
            disabled={foSyncing}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            data-testid="sync-flexoffers-commission-btn"
            title="Re-pull FlexOffers conversions for the selected date range and override existing commission values with the latest from FlexOffers."
          >
            <CurrencyDollar size={16} className={`mr-1.5 ${foSyncing ? "animate-spin" : ""}`} />
            {foSyncing ? "Syncing FlexOffers…" : "Sync FlexOffers Commissions"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={syncShopnomix}
            disabled={snSyncing}
            className="border-lime-200 text-lime-800 hover:bg-lime-50 hover:text-lime-900"
            data-testid="sync-shopnomix-report-btn"
            title="Pull Shopnomix CPA conversions. Uses the date filter above, or last 30 days if All time. Max 90 days per sync."
          >
            <ArrowsClockwise size={16} className={`mr-1.5 ${snSyncing ? "animate-spin" : ""}`} />
            {snSyncing ? "Syncing Shopnomix…" : "Sync Shopnomix"}
          </Button>
          <Button onClick={syncActions} disabled={syncing} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="sync-actions-btn">
            <ArrowsClockwise size={16} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync Actions"}
          </Button>
        </div>
      </div>

      {/* Filter bar — pub + brand + state + date range */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="admin-reports-filters">
        <Funnel size={16} className="text-slate-400" />
        <MultiSelect
          value={pubFilter}
          onChange={setPubFilter}
          options={publisherOptions}
          allLabel="All publishers"
          menuLabel="Publishers"
          testId="filter-publisher"
        />
        {/* Searchable — the facet list is ~2.4k brands, which is not
            scrollable in practice (and rendered every option into the DOM).
            Same component the publisher Reports filters already use. */}
        <BrandCombobox
          multiple
          value={brandFilter}
          onChange={setBrandFilter}
          brands={brandOptions}
          testId="filter-brand"
          placeholder="All brands"
          width="w-[180px]"
        />
        {/* NON_COMMISSION / BONUS filter on `network_state` (raw upstream)
            rather than `state` (canonical billing) — Non-Commission rows
            would otherwise be invisible, since they collapse into REVERSED
            canonically. buildParams routes each value to the right param,
            so Approved + Non-Commission together is now expressible. */}
        <MultiSelect
          value={stateFilter}
          onChange={setStateFilter}
          options={STATE_OPTIONS}
          allLabel="Any state"
          menuLabel="State"
          testId="filter-state"
          className="w-[160px]"
        />
        <FeedPartnerSelect
          value={networkFilter}
          onChange={setNetworkFilter}
          testId="filter-network"
          summarizeExcept
        />
        <DateRangePresets
          preset={dateRange.preset}
          start={dateRange.start}
          end={dateRange.end}
          onChange={setDateRange}
          testId="filter-date"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 ml-auto" data-testid="columns-toggle-btn">
              <Columns size={14} className="mr-1.5" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="columns-menu">
            <DropdownMenuLabel>Show columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {REPORT_COLUMNS.filter((c) => !c.optional).map((c) => (
              <DropdownMenuCheckboxItem
                key={c.id}
                checked={!!visibleCols[c.id]}
                onCheckedChange={() => toggleColumn(c.id)}
                onSelect={(e) => e.preventDefault()}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
              Extra money fields
            </DropdownMenuLabel>
            {REPORT_COLUMNS.filter((c) => c.optional).map((c) => (
              <DropdownMenuCheckboxItem
                key={c.id}
                checked={!!visibleCols[c.id]}
                onCheckedChange={() => toggleColumn(c.id)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`col-toggle-${c.id}`}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
        {[
          {
            label: "Total Actions",
            value: ((stats.total) || 0).toLocaleString(),
            hint: "pending + approved + reversed",
          },
          { label: "Pending", value: (stats.pending || 0).toLocaleString(), color: "text-amber-600" },
          { label: "Approved", value: (stats.approved || 0).toLocaleString(), color: "text-emerald-600" },
          { label: "Reversed", value: (stats.reversed || 0).toLocaleString(), color: "text-red-600" },
          // Non-Commission tile — counted off `network_state` so the
          // row count matches FlexOffers' own dashboard. Hidden inside
          // Reversed in the canonical state breakdown.
          {
            label: "Non-Commission",
            value: (stats.non_commissionable || 0).toLocaleString(),
            color: "text-slate-500",
            hint: stats.bonus ? `+ ${stats.bonus.toLocaleString()} bonus` : "billing-excluded",
          },
          // Sale Amount + Total Payout — mirrors FlexOffers' headline
          // tiles (Orders / Sales Amount / Commissions) so admin can
          // diff against the network dashboard line-for-line.
          {
            label: "Sale Amount",
            value: `$${(stats.sale_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
            hint: (networkFilter === "pubadhub" || networkFilter === "pubadhub_kevin")
              ? "N/A — PubAdHub reports commission only, not order GMV"
              // On a reversed-only filter the backend switches these two
              // tiles to a gross basis (netting reversals out of a
              // reversed-only set would render $0.00), so the hint has to
              // switch with it rather than assert an exclusion that is not
              // being applied.
              : (isReversedOnlyFilter ? "reversed only" : "excl. reversed"),
          },
          {
            label: "Total Payout",
            value: `$${(stats.payout || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
            hint: isReversedOnlyFilter
              ? "reversed only"
              : (stats.reversed ? `excl. ${stats.reversed.toLocaleString()} reversed` : "excl. reversed"),
          },
          // Pub Earning + Margin — only when a single publisher is selected,
          // because the backend only computes `pub_earning_total` then (it
          // routes through the publisher's OWN engine, summarise_by_brand, so
          // the figure is identical to what that publisher sees on their
          // dashboard rather than a second, separately-derived number).
          //
          // Shown next to Total Payout on purpose. The two were being read as
          // if they should agree, but one is revenue IN (what the network
          // pays us) and the other is cost OUT (what the publisher earns).
          // Putting the margin between them makes that legible instead of
          // looking like a reconciliation failure.
          ...(pubFilter.length === 1 && stats.pub_earning_total != null ? [
            {
              label: "Pub Earning",
              value: `$${(stats.pub_earning_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
              hint: "matches this publisher's dashboard",
            },
            {
              label: "Margin",
              value: `$${((stats.payout || 0) - (stats.pub_earning_total || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
              color: ((stats.payout || 0) - (stats.pub_earning_total || 0)) < 0
                ? "text-rose-600" : "text-emerald-700",
              hint: "payout − pub earning",
            },
          ] : []),
        ].map((m) => (
          <div key={m.label} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`admin-stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{m.label}</div>
            <div className={`text-lg font-bold font-mono ${m.color || "text-slate-900"}`}>{m.value}</div>
            {m.hint ? (
              <div className="text-[10px] text-slate-400 mt-1 tracking-wide" data-testid={`admin-stat-${m.label.toLowerCase().replace(/\s/g, '-')}-hint`}>{m.hint}</div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                {activeColumns.map((c) => (
                  <th
                    key={c.id}
                    className={`${c.align === "right" ? "text-right" : "text-left"} px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const pubEmail = publisherForAction(a);
                const marginVal = a.margin;
                const marginTone =
                  marginVal == null ? "text-slate-500"
                    : marginVal >= 0 ? "text-emerald-600" : "text-red-500";
                const cell = (colId) => {
                  switch (colId) {
                    case "action_id":
                      return <td key={colId} className="px-4 py-2.5 font-mono text-xs text-slate-500">{a.action_id}</td>;
                    case "brand":
                      return <td key={colId} className="px-4 py-2.5 text-slate-900">{a.campaign_name}</td>;
                    case "network":
                      return (
                        <td key={colId} className="px-4 py-2.5">
                          {a.network ? (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                                (a.network || "").toLowerCase() === "impact" ? "bg-blue-50 text-blue-700 ring-1 ring-blue-100" :
                                (a.network || "").toLowerCase() === "skimlinks" ? "bg-amber-50 text-amber-700 ring-1 ring-amber-100" :
                                (a.network || "").toLowerCase() === "mrge" ? "bg-violet-50 text-violet-700 ring-1 ring-violet-100" :
                                (a.network || "").toLowerCase() === "flexoffers" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100" :
                                (a.network || "").toLowerCase() === "partnerize" ? "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100" :
                                (a.network || "").toLowerCase() === "cj" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100" :
                                (a.network || "").toLowerCase() === "admedia" ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100" :
                                (a.network || "").toLowerCase() === "pubadhub" ? "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-100" :
                                (a.network || "").toLowerCase() === "shopnomix_kevin" ? "bg-orange-50 text-orange-700 ring-1 ring-orange-100" :
                                (a.network || "").toLowerCase() === "siteplug" ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100" :
                                (a.network || "").toLowerCase() === "pronomix" ? "bg-purple-50 text-purple-700 ring-1 ring-purple-100" :
                                (a.network || "").toLowerCase() === "cashbackhive" ? "bg-teal-50 text-teal-700 ring-1 ring-teal-100" :
                                (a.network || "").toLowerCase() === "fatcoupon" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100" :
                                (a.network || "").toLowerCase() === "giddyup" ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-100" :
                                (a.network || "").toLowerCase().startsWith("rga") ? "bg-violet-50 text-violet-700 ring-1 ring-violet-100" :
                                "bg-slate-50 text-slate-700 ring-1 ring-slate-100"
                              }`}
                              data-testid={`network-badge-${a.action_id}`}
                            >
                              {networkPillLabel(a.network)}
                            </span>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      );
                    case "publisher":
                      return <td key={colId} className="px-4 py-2.5 text-slate-700 text-xs">{pubEmail || <span className="text-slate-400">—</span>}</td>;
                    case "sub_id":
                      return <td key={colId} className="px-4 py-2.5 font-mono text-xs font-bold text-[#002FA7]">{subIdForAction(a) || "-"}</td>;
                    case "state":
                      return (
                        <td key={colId} className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            a.state === "APPROVED" ? "bg-emerald-50 text-emerald-700" :
                            a.state === "PENDING" ? "bg-amber-50 text-amber-700" :
                            "bg-red-50 text-red-700"
                          }`}>{a.state}</span>
                          {a.over_cap && (
                            <span
                              className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-rose-100 text-rose-800 ring-1 ring-rose-200"
                              data-testid={`over-cap-badge-${a.action_id}`}
                              title={a.over_cap_reason || "Over daily sale cap — pub earning $0"}
                            >
                              Over cap
                            </span>
                          )}
                        </td>
                      );
                    case "amount":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono">{fmtMoney(a.amount)}</td>;
                    case "payout":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono">{fmtMoney(a.payout)}</td>;
                    case "network_pct":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono text-slate-500">{fmtPct(a.network_pct)}</td>;
                    case "pub_commission_pct":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[#002FA7]">{fmtPct(a.pub_commission_pct)}</td>;
                    case "pub_commission":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono text-slate-700">{fmtMoney(a.pub_commission)}</td>;
                    case "pub_earning":
                      return <td key={colId} className="px-4 py-2.5 text-right font-mono font-semibold text-[#002FA7]">{fmtMoney(a.pub_earning)}</td>;
                    case "margin":
                      return <td key={colId} className={`px-4 py-2.5 text-right font-mono ${marginTone}`}>{fmtMoney(a.margin)}</td>;
                    case "margin_pct":
                      return <td key={colId} className={`px-4 py-2.5 text-right font-mono ${marginTone}`}>{fmtPct(a.margin_pct)}</td>;
                    case "country":
                      return (
                        <td key={colId} className="px-4 py-2.5 text-slate-600" data-testid={`country-${a.action_id}`}>
                          {a.brand_country ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-base leading-none">{iso2ToFlag(a.brand_country)}</span>
                              <span className="font-mono text-xs text-slate-700">{a.brand_country}</span>
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    case "date":
                      return <td key={colId} className="px-4 py-2.5 text-slate-500 text-xs">{a.event_date?.substring(0, 10) || "-"}</td>;
                    default:
                      return null;
                  }
                };
                return (
                  <tr key={a.action_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`action-row-${a.action_id}`}>
                    {activeColumns.map((c) => cell(c.id))}
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr><td colSpan={Math.max(activeColumns.length, 1)} className="px-4 py-12 text-center text-slate-400 text-sm">
                  {total === 0 ? "No actions match the current filters." : ""}
                </td></tr>
              )}
              {loading && items.length === 0 && (
                <tr><td colSpan={Math.max(activeColumns.length, 1)} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div
            className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between"
            data-testid="admin-reports-pagination"
          >
            <div className="text-xs text-slate-500" data-testid="admin-reports-page-info">
              Showing <span className="font-mono font-semibold text-slate-700">{showingFrom.toLocaleString()}–{showingTo.toLocaleString()}</span> of <span className="font-mono font-semibold text-slate-700">{total.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" disabled={page <= 1 || loading}
                onClick={() => goToPage(page - 1)}
                data-testid="admin-reports-prev-page"
              >
                <CaretLeft size={14} className="mr-1" /> Prev
              </Button>
              <span className="text-xs text-slate-500 font-mono px-2">Page {page} / {totalPages}</span>
              <Button
                variant="outline" size="sm" disabled={page >= totalPages || loading}
                onClick={() => goToPage(page + 1)}
                data-testid="admin-reports-next-page"
              >
                Next <CaretRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
